import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = <>
    <h3 className="text-2xl font-serif text-slate-50 mb-6" data-id="element-435">Privacy Policy</h3>
    <div className="space-y-4 text-slate-300 leading-relaxed text-sm" data-id="element-436">
      <p data-id="element-437">
        <strong className="text-slate-100" data-id="element-438">Last Updated:</strong> January 2025
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-439">
        Information We Collect
      </h4>
      <p data-id="element-440">
        When you purchase "The Cortisol Switch," we collect only the information
        necessary to process your order: your name, email address, and payment
        details. Payment processing is handled securely by our third-party
        payment processor — we never store your full credit card information on
        our servers.
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-441">
        How We Use Your Information
      </h4>
      <p data-id="element-442">
        Your information is used solely to deliver your digital product, send
        your purchase confirmation, and provide customer support if needed. We
        do not sell, rent, or share your personal information with third parties
        for marketing purposes.
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-443">Cookies</h4>
      <p data-id="element-444">
        Our website may use essential cookies to ensure proper functionality and
        analytics cookies to understand how visitors interact with our page. You
        can disable cookies in your browser settings at any time.
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-445">Data Security</h4>
      <p data-id="element-446">
        We implement industry-standard security measures including SSL
        encryption to protect your personal information during transmission and
        storage.
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-447">Your Rights</h4>
      <p data-id="element-448">
        You have the right to access, correct, or delete your personal data at
        any time. You may also unsubscribe from any email communications using
        the link provided in each email.
      </p>
    </div>
  </>;
const termsContent = <>
    <h3 className="text-2xl font-serif text-slate-50 mb-6" data-id="element-449">
      Terms & Conditions
    </h3>
    <div className="space-y-4 text-slate-300 leading-relaxed text-sm" data-id="element-450">
      <p data-id="element-451">
        <strong className="text-slate-100" data-id="element-452">Last Updated:</strong> January 2025
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-453">
        Product Description
      </h4>
      <p data-id="element-454">
        "The Cortisol Switch" is a digital educational manual provided in PDF
        format. It is designed for informational and educational purposes only
        and is not intended to replace professional medical advice, diagnosis,
        or treatment.
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-455">
        Medical Disclaimer
      </h4>
      <p data-id="element-456">
        The content within this manual is based on research and general wellness
        principles. Always consult with a qualified healthcare professional
        before making changes to your diet, exercise routine, or health regimen.
        Individual results may vary.
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-457">
        Digital Delivery
      </h4>
      <p data-id="element-458">
        Upon successful payment, you will receive immediate access to download
        the digital product. Due to the nature of digital products, all sales
        are final once the download link has been accessed.
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-459">
        Intellectual Property
      </h4>
      <p data-id="element-460">
        All content within "The Cortisol Switch" is protected by copyright. You
        may not reproduce, distribute, or resell any part of this product
        without prior written consent.
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-461">
        Limitation of Liability
      </h4>
      <p data-id="element-462">
        We provide this product "as is" and make no guarantees regarding
        specific health outcomes. By purchasing, you acknowledge that results
        depend on individual circumstances and consistent application of the
        methods described.
      </p>
    </div>
  </>;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-8 bg-slate-900 border-t border-slate-800" data-id="element-463">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-464">
          <p className="text-slate-500 text-sm" data-id="element-465">
            © {new Date().getFullYear()} The Cortisol Switch. All rights
            reserved.
          </p>
          <div className="flex items-center gap-6" data-id="element-466">
            <button onClick={() => setActiveModal('privacy')} className="text-slate-500 text-sm hover:text-slate-300 transition-colors" data-id="element-467">
              Privacy Policy
            </button>
            <button onClick={() => setActiveModal('terms')} className="text-slate-500 text-sm hover:text-slate-300 transition-colors" data-id="element-468">
              Terms & Conditions
            </button>
          </div>
        </div>
      </footer>

      {/* Modal Overlay */}
      <AnimatePresence data-id="element-469">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-470">
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-slate-800 border border-slate-700 rounded-2xl p-6 md:p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-471">
              <div className="flex items-center justify-between mb-6" data-id="element-472">
                <div className="w-8" data-id="element-473" />
                <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-700 transition-colors text-slate-400 hover:text-slate-200" data-id="element-474">
                  <X className="w-5 h-5" data-id="element-475" />
                </button>
              </div>
              {activeModal === 'privacy' ? privacyContent : termsContent}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}